<app-portal-navigation
  [portalName]="portalName"
  [portalIcon]="portalIcon"
  [portalClass]="portalClass"
  collapseId="createBookingMenu"
  [firstName]="userProfile?.firstName ?? ''"
  [lastName]="userProfile?.lastName ?? ''"
  [primaryItems]="primaryNavigationItems"
  [profileRoute]="profileRoute"
  [additionalItems]="additionalNavigationItems"
>
</app-portal-navigation>

<div class="container-fluid p-3 p-md-4 create-booking-page" style="max-width: 1300px">
  <!-- Hero Banner -->
  <div class="booking-hero card border-0 mb-4">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Practice Reception</div>
        <h2 class="booking-hero__title mb-1">Create Walk-In Booking</h2>
        <div class="booking-hero__meta">
          <span
            ><i class="bi bi-calendar2-plus me-1 text-primary"></i>Register a patient appointment
            with practice operating hour validation</span
          >
        </div>
      </div>
      <div class="booking-hero__status">
        <button class="btn btn-outline-secondary" (click)="cancel()">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>
  </div>

  <!-- Prominent Error Alert Banner -->
  @if (errorMessage) {
    <div
      class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
      role="alert"
    >
      <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
      <div class="flex-grow-1">
        <div class="fw-bold text-danger">Booking Submission Error</div>
        <div class="small text-dark mt-1">{{ errorMessage }}</div>
      </div>
      <button
        type="button"
        class="btn-close ms-2"
        (click)="errorMessage = null"
        aria-label="Close error message"
      ></button>
    </div>
  }

  <form
    #bookingForm="ngForm"
    (ngSubmit)="bookingForm.valid && isBookingScheduleValid() && submit()"
  >
    <div class="alert alert-info d-flex align-items-start mb-4">
      <i class="bi bi-info-circle-fill me-2 mt-1 fs-5"></i>
      <div>
        Select the consulting doctor, enter the patient details, and assign an available time slot
        within the operating schedule.
      </div>
    </div>

    <div class="row g-4">
      <!-- Patient Details Card -->
      <div class="col-12 col-lg-6">
        <div class="card booking-card h-100">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Step 1</div>
              <h4 class="sub-title mb-0">Patient Details</h4>
            </div>
          </div>

          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12 col-sm-6">
                <label class="form-label">First Name <span class="text-danger">*</span></label>
                <input
                  id="patientFirstName"
                  type="text"
                  class="form-control"
                  name="patientFirstName"
                  [(ngModel)]="form.patientFirstName"
                  required
                  placeholder="e.g. John"
                  #firstName="ngModel"
                />
                @if (firstName.invalid && firstName.touched) {
                  <div class="text-danger small mt-1">First name is required.</div>
                }
              </div>

              <div class="col-12 col-sm-6">
                <label class="form-label">Last Name <span class="text-danger">*</span></label>
                <input
                  id="patientLastName"
                  type="text"
                  class="form-control"
                  name="patientLastName"
                  [(ngModel)]="form.patientLastName"
                  required
                  placeholder="e.g. Doe"
                  #lastName="ngModel"
                />
                @if (lastName.invalid && lastName.touched) {
                  <div class="text-danger small mt-1">Last name is required.</div>
                }
              </div>

              <div class="col-12 col-sm-6">
                <label class="form-label">Phone Number</label>
                <input
                  id="patientPhone"
                  type="tel"
                  class="form-control"
                  name="patientPhone"
                  [(ngModel)]="form.patientPhone"
                  placeholder="e.g. 0821234567"
                />
              </div>

              <div class="col-12 col-sm-6">
                <label class="form-label"
                  >Patient ID
                  <span class="text-muted small fw-normal">(Optional Link)</span>
                </label>
                <input
                  id="patientCode"
                  type="text"
                  class="form-control"
                  name="patientCode"
                  [(ngModel)]="form.patientCode"
                  (keyup)="onSearch()"
                  placeholder="Link existing patient code"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Doctor & Consultation Details Card -->
      <div class="col-12 col-lg-6">
        <div class="card booking-card h-100">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Step 2</div>
              <h4 class="sub-title mb-0">Doctor & Visit Purpose</h4>
            </div>
          </div>

          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Practitioner <span class="text-danger">*</span></label>

                @if (selectedDoctor) {
                  <div
                    class="d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <i class="bi bi-person-badge-fill fs-4 text-primary"></i>
                      <div>
                        <span class="fw-bold text-dark"
                          >Dr. {{ selectedDoctor.firstName }} {{ selectedDoctor.lastName }}</span
                        >
                        @if (selectedDoctor.specialization) {
                          <span
                            class="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill ms-2"
                            >{{ selectedDoctor.specialization }}</span
                          >
                        }
                      </div>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      (click)="clearDoctorSelection()"
                      title="Change Doctor"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                } @else {
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-search"></i></span>
                    <input
                      id="doctorSearchTerm"
                      type="text"
                      class="form-control"
                      name="doctorSearchTerm"
                      [(ngModel)]="doctorSearchTerm"
                      placeholder="Search by name, specialization..."
                      (keyup.enter)="searchDoctors()"
                    />
                    <button
                      type="button"
                      class="btn btn-primary"
                      (click)="searchDoctors()"
                      [disabled]="isSearchingDoctors"
                    >
                      @if (isSearchingDoctors) {
                        <span class="spinner-border spinner-border-sm" role="status"></span>
                      } @else {
                        Search
                      }
                    </button>
                  </div>

                  @if (doctorSearchResults.length > 0) {
                    <div
                      class="doctor-search-results mt-2 border rounded-3 shadow-sm overflow-hidden"
                    >
                      @for (doctor of doctorSearchResults; track doctor.doctorId) {
                        <div
                          class="p-2 px-3 border-bottom d-flex align-items-center justify-content-between doctor-search-result"
                          (click)="selectDoctor(doctor)"
                        >
                          <div>
                            <span class="fw-bold text-dark"
                              >Dr. {{ doctor.firstName }} {{ doctor.lastName }}</span
                            >
                            @if (doctor.specialization) {
                              <span class="text-muted ms-2 small">{{ doctor.specialization }}</span>
                            }
                          </div>
                          <span class="btn btn-sm btn-outline-primary py-0">Select</span>
                        </div>
                      }
                    </div>
                  }

                  @if (
                    doctorSearchAttempted && doctorSearchResults.length === 0 && !isSearchingDoctors
                  ) {
                    <div class="text-muted small mt-1">No doctors found matching query.</div>
                  }
                }
              </div>

              <div class="col-12">
                <label class="form-label">Booking Type <span class="text-danger">*</span></label>
                <select
                  id="bookingType"
                  class="form-select"
                  name="bookingType"
                  [(ngModel)]="form.bookingType"
                  required
                  #bookingType="ngModel"
                >
                  @for (type of bookingTypes; track type) {
                    <option [value]="type">{{ type | titlecase }}</option>
                  }
                </select>
                @if (bookingType.invalid && bookingType.touched) {
                  <div class="text-danger small mt-1">Booking type is required.</div>
                }
              </div>

              <div class="col-12">
                <label class="form-label">Reason for Visit</label>
                <input
                  id="reasonForVisit"
                  type="text"
                  class="form-control"
                  name="reasonForVisit"
                  [(ngModel)]="form.reasonForVisit"
                  placeholder="e.g. Routine checkup, Follow-up consultation"
                />
              </div>

              <div class="col-12">
                <label class="form-label">Notes</label>
                <textarea
                  id="bookingNotes"
                  class="form-control"
                  name="notes"
                  rows="2"
                  [(ngModel)]="form.notes"
                  placeholder="Optional notes for the doctor..."
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Booking Slot Card -->
      <div class="col-12">
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Step 3</div>
              <h4 class="sub-title mb-0">Appointment Slot & Schedule</h4>
            </div>
          </div>

          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12 col-sm-6 col-md-4">
                <label class="form-label"
                  >Appointment Date <span class="text-danger">*</span></label
                >
                <input
                  id="slotDate"
                  type="date"
                  class="form-control"
                  name="slotDate"
                  [(ngModel)]="form.bookingSlot.slotDate"
                  [min]="minimumBookingDate"
                  required
                  #slotDate="ngModel"
                />
                @if (slotDate.invalid && slotDate.touched) {
                  <div class="text-danger small mt-1">Date is required.</div>
                }
                @if (slotDate.touched && !isSlotDateAllowed()) {
                  <div class="text-danger small mt-1">{{ slotDateValidationMessage }}</div>
                }
              </div>

              <div class="col-12 col-sm-6 col-md-4">
                <label class="form-label"
                  >Appointment Time <span class="text-danger">*</span></label
                >
                <input
                  id="slotTime"
                  type="time"
                  class="form-control"
                  name="slotTime"
                  [(ngModel)]="form.bookingSlot.slotTime"
                  [min]="slotTimeMin"
                  [max]="slotTimeMax"
                  required
                  #slotTime="ngModel"
                />
                @if (slotTime.invalid && slotTime.touched) {
                  @if (slotTime.errors?.['required']) {
                    <div class="text-danger small mt-1">Time is required.</div>
                  }
                  @if (slotTime.errors?.['min'] || slotTime.errors?.['max']) {
                    <div class="text-danger small mt-1">Time must be within practice hours.</div>
                  }
                }
                @if (slotTime.touched && !isSlotTimeWithinOperatingHours()) {
                  <div class="text-danger small mt-1">{{ slotTimeValidationMessage }}</div>
                }
                <div class="text-muted small mt-1">
                  {{ slotTimeRangeLabel }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions Sticky Bar -->
    <div class="actions actions--sticky d-flex justify-content-between align-items-center mt-4">
      <button type="button" class="btn btn-outline-secondary px-4" (click)="cancel()">
        Cancel
      </button>

      <button
        type="submit"
        class="btn btn-primary px-4"
        [disabled]="
          isSubmitting || bookingForm.invalid || !form.doctorId || !isBookingScheduleValid()
        "
      >
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>Creating...
        } @else {
          <i class="bi bi-check2-circle me-1"></i> Create Booking
        }
      </button>
    </div>
  </form>
</div>
